import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import Constant from "../Constant";
import { InventoryApiService } from "./InventoryApiService";
import { SupportApiService } from "./SupportApiService";
import { RouteAccessControl } from "../Config/RouteConfig";

export const CommonService = {
  Toast: Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 6000,
    timerProgressBar: true,
    width: '500px',
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
  }),

  showConfirmation,
  formatDateTime,
  isAdmin,
  isUser,
  isWarehouse,
  getRole,
  getCompanyId,
  getCompanyName,
  getUserId,
  getProfile,
  showPdf,
  formatTime,
  formatDate,
  numberFormatter,
  formatToSingleDecimal,
  formatRecentActivityDate,
  formatRecentActivityTime,
  convertToEST,
  isSameUser,
  isSameCompany,
  formatRoleName,
  getDashboardPrefrence,
  formatTimeForSupportRequest,
  formatDateForSupportRequest,
  isFacilityMatch,
  formatStrictDate,
  formatStrictTime,
  getTodayInTimeZone,
  calculateAge,
  transformStatusList,
  formatStatus,
  formatDateToReadableString,
  formatDateWithoutTimezone,
  isUserLoggedIn,
  getSignupStatus,
  getPreviousMonthDateRange,
  canAccessRoute,
  isRouteAllowed
};

async function showConfirmation() {
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "No, cancel!",
  });

  return result.isConfirmed;
}

function convertToEST(dateTimeString: string): string {
  // Define regex pattern to match the input format
  const dateTimePattern = /^(.*), (\d{4}) \| (\d{2}):(\d{2}) (AM|PM)$/;
  const match = dateTimeString.match(dateTimePattern);

  if (match) {
    // Extract the parts from the regex match
    const [, monthDay, year, hour, minute, period] = match;
    const dateString = `${monthDay}, ${year} ${hour}:${minute} ${period}`;

    // Parse the date string to a Date object
    const dateTime = new Date(dateString);

    // Check if period is PM and adjust the hour
    let hours = parseInt(hour, 10);
    if (period === "PM" && hours < 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    // Construct the new Date object in UTC
    const utcDate = new Date(
      Date.UTC(
        dateTime.getFullYear(),
        dateTime.getMonth(),
        dateTime.getDate(),
        hours,
        parseInt(minute, 10)
      )
    );

    // Format the date and time to EST
    const optionsDate: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const optionsTime: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    const formattedDate = utcDate.toLocaleDateString("en-US", optionsDate);
    const formattedTime = utcDate.toLocaleTimeString("en-US", optionsTime);

    return `${formattedDate} | ${formattedTime}`;
  }

  // Return an error message if the input string is not in the expected format
  return "";
}

function formatDateTime(dateTimeString: any): any {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const optionsDate: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const optionsTime: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    const formattedDate = dateTime.toLocaleDateString("en-US", optionsDate);
    const formattedTime = dateTime.toLocaleTimeString("en-US", optionsTime);

    return `${formattedDate}, ${formattedTime}`;
  } else {
    return "";
  }
}

function formatStrictDate(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const optionsDate: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    };

    return dateTime.toLocaleDateString("en-US", optionsDate);
  } else {
    return "";
  }
}

function getTodayInTimeZone() {
  const now = new Date();

  // Using "en-CA" because it formats dates as YYYY-MM-DD
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.format(now); 

  return parts; // safe normalized "today" at midnight in that TZ
}

function isAdmin() {
  const cookies = new Cookies();
  const role = cookies.get("userRole");
  return role == "SUPER_ADMIN" ? true : false;
}

function isUser() {
  const cookies = new Cookies();
  const role = cookies.get("userRole");

  if (role == "ADMIN" || role == "USER") return true;
  else return false;
}

function isWarehouse() {
  const cookies = new Cookies();
  const role = cookies.get("userRole");
  return role == "WAREHOUSE" ? true : false;
}

function getRole() {
  const cookies = new Cookies();
  const userRole = cookies.get("userRole");
  return userRole;
}

function getCompanyId() {
  const cookies = new Cookies();
  const companyId = cookies.get("companyId");
  return companyId;
}

function getCompanyName() {
  const cookies = new Cookies();
  const companyName = cookies.get("companyName");
  return companyName;
}

function getUserId() {
  const cookies = new Cookies();
  const userId = cookies.get("userId");
  return userId;
}

function getProfile() {
  const profile = localStorage.getItem("profile");
  return profile;
}

function getDashboardPrefrence() {
  const dashboardPrefrence = localStorage.getItem("dashboardPrefrence");
  return dashboardPrefrence;
}

// async function showPdf(id: any, companyId: any, siteId: any, fileName: any) {
//   try {
//     const response: any = await InventoryApiService.showPDF(
//       id,
//       companyId,
//       siteId,
//       fileName
//     );
//     const dataUrl = URL.createObjectURL(response.data);

//     Swal.fire({
//       html: `<iframe src="${dataUrl}" width="100%" height="600px"></iframe>`,
//       width: "80%",
//       grow: "fullscreen",
//       showCloseButton: true,
//       showConfirmButton: false,
//     });
//   } catch (error) {
//     console.error("Error fetching PDF:", error);
//     Swal.fire("Error", "Failed to load PDF", "error");
//   }
// }

async function showPdf(
  id: any,
  companyId: any,
  siteId: any,
  fileName: any,
  requestType: any,
  requestNumber: any
) {
  try {
    let response;

    if (requestType === Constant.RequestType.SAMPLE_REQUEST) {
      response = await InventoryApiService.showPDFForSample(
        id,
        companyId,
        siteId,
        fileName,
        requestNumber
      );
    } else if (requestType === Constant.RequestType.OFFER_REQUEST) {
      response = await InventoryApiService.showPDFForOffer(
        id,
        companyId,
        siteId,
        fileName,
        requestNumber
      );
    } else if (requestType === Constant.RequestType.ORDER_REQUEST) { // Fixed condition
      response = await InventoryApiService.showPDFForOrder(
        id,
        companyId,
        siteId,
        fileName,
        requestNumber
      );
    } else if (requestType === Constant.RequestType.SELL_REQUEST) {
      response = await InventoryApiService.showPDFForSellRequest(
        id,
        companyId,
        siteId,
        fileName,
        requestNumber
      );
    } else if (requestType === Constant.RequestType.CUSTOMER_SERVICE_REQUEST) {

      const request = {
        id: id,
        companyId: companyId,
        siteId: siteId,
        fileName: fileName,
        requestNumber
      }
      response = await SupportApiService.viewCustomerServiceRequestPDF(request);

    } else {
      throw new Error("Invalid request type");
    }

   
    const dataUrl = URL.createObjectURL(response.data);

    Swal.fire({
      html: `<iframe src="${dataUrl}" width="100%" height="600px"></iframe>`,
      width: "80%",
      grow: "fullscreen",
      showCloseButton: true,
      showConfirmButton: false,
    });
  } catch (error) {
    console.error("Error fetching PDF:", error);
    Swal.fire("Error", "Failed to load PDF", "error");
  }
}

function formatDateWithoutTimezone(dateTime: string | null | undefined): string {
    if (!dateTime) return "";
    const date = new Date(dateTime);

    if (date) {
      const options: any = {
        month: "short",
        day: "numeric",
        year: "numeric",
      };
      return date.toLocaleString("en-US", options);
    } else {
      return "";
    }
}

function formatDate(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const now = new Date();

    const isToday = dateTime.toDateString() === now.toDateString();
    const isYesterday =
      dateTime.toDateString() ===
      new Date(now.setDate(now.getDate() - 1)).toDateString();

    if (isToday) {
      return ""; // Return empty string for today's date
    } else if (isYesterday) {
      return "Yesterday";
    } else {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
      };
      return dateTime.toLocaleDateString("en-US", options);
    }
  } else {
    return "";
  }
}



function formatDateForSupportRequest(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    return dateTime.toLocaleDateString("en-US", options);
  } else {
    return "";
  }
}



function formatTime(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - dateTime.getTime()) / 1000
    );

    const minutes = Math.floor(diffInSeconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

    if (days > 0) {
      const options: Intl.DateTimeFormatOptions = {
        timeZone: "America/New_York",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      };
      return dateTime.toLocaleTimeString("en-US", options);
    } else if (hours > 0) {
      return rtf.format(-hours, "hour");
    } else if (minutes > 0) {
      return rtf.format(-minutes, "minute");
    } else {
      return "just now";
    }
  } else {
    return "";
  }
}

function formatStrictTime(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const optionsTime: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    return dateTime.toLocaleTimeString("en-US", optionsTime);
  } else {
    return "";
  }
}

function formatTimeForSupportRequest(dateTimeString: string | null | undefined): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);

    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    return dateTime.toLocaleTimeString("en-US", options);
  } else {
    return "";
  }
}


function numberFormatter(value: any) {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "decimal", // Use 'decimal' style instead of 'currency'
    maximumFractionDigits: 2, // Adjust this to control the number of decimal places
  });
  return formatter.format(value);
}

function formatToSingleDecimal(value: any) {
  const number = parseFloat(value);
  return Number.isNaN(number) ? "" : parseFloat(number.toFixed(1));
};


function formatRecentActivityDate(dateTimeString: any): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    return dateTime.toLocaleString("en-US", options);
  } else {
    return "";
  }
}

function formatRecentActivityTime(dateTimeString: any): string {
  if (dateTimeString) {
    const dateTime = new Date(dateTimeString);
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return dateTime.toLocaleString("en-US", options);
  } else {
    return "";
  }
}

function isSameUser(userId: any): boolean {
  const cookies = new Cookies();
  const uId = cookies.get("userId");
  return userId === uId;
}

function isSameCompany(companyId: any): boolean {
  const cookies = new Cookies();
  const comId = cookies.get("companyId");
  return companyId === comId;
}

// Utility function to check if a facility matches any name in the Constant.facility array
function isFacilityMatch(facilityName: string): Boolean {
  return Object.values(Constant.facility).some(name => {
    return facilityName.toLowerCase() === name.toLowerCase();
  });
};

// Function to format role names
function formatRoleName(roleName: string): string {
  if (!roleName) return "-";

  // Split the role name by underscores and capitalize each word
  return roleName
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatStatus(status: string): string[] {
  if (!status) return ["-"]; // Return an array with "-" if status is empty

  return status.split(",").map((roleName) =>
    roleName
      .trim()
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export const buildQueryParams = (params: Record<string, any>): string => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
          query.append(key, value.toString());
      }
  });
  return query.toString();
};

function formatDateToReadableString (inputDate: string): string {
  const date = new Date(inputDate);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short', 
    day: '2-digit'  
  };

  return date.toLocaleDateString('en-US', options);
};


function calculateAge(timestamp: string): any {

  // Get current date in UTC
  const now = new Date(Date.now());  // Default `Date.now()` gives the current time in UTC
  const birthDate = new Date(timestamp); // Timestamp is already in UTC

  // Get the difference in years and months using UTC
  let years = now.getUTCFullYear() - birthDate.getUTCFullYear();
  let months = now.getUTCMonth() - birthDate.getUTCMonth();

  // If birth month hasn't occurred yet this year, reduce the age by 1 year
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  // Adjust for the days in the current month (UTC)
  const currentDay = now.getUTCDate();
  const birthDay = birthDate.getUTCDate();
  if (currentDay < birthDay) {
    months -= 1;
  }

  // If months goes negative, adjust years again (UTC)
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  return { years, months };
};

function isUserLoggedIn() {
  const cookies = new Cookies();
  const token = cookies.get("authToken");
  return token ? true : false;
}

function getSignupStatus () {
  return localStorage.getItem("signupStatus");
}

function getPreviousMonthDateRange() {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const endDate = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    startDate,
    endDate,
    startDateText: startDate.toLocaleDateString("en-CA"),
    endDateText: endDate.toLocaleDateString("en-CA"),
  };
};

function transformStatusList (statusList:any) {
  return statusList.map(item => {
    switch (item.status) {
      case "PENDING_REVIEW":
        return "Pending Review";
      case "ACCEPTED":
        return "Accepted";
      case "DELIVERED":
        return "Delivered";
      case "REJECTED":
        return "Rejected";
      case "OWNERSHIP_TRANSFERRED":
        return "Ownership Transferred";
      case "CLOSED":
        return "Closed";
      case "UNDER_REVIEW":
        return "Under Review";
      case "IN_PROGRESS":
        return "In Progress";
      case "CANCELLED":
        return "Cancelled";
      case "UPLOADED":
        return "Uploaded";
      case "FINALIZED":
        return "Finalized";
      case "PUBLISHED":
        return "Published";
      case "SOLD":
        return "Sold";
      case "UNPUBLISHED":
        return "Unpublished";
      case "REPUBLISHED":
        return "Republished";
      case "SEND_TO_OWNER":
        return "Send to Owner";
      case "ADMIN_ACCEPTED":
        return "Admin Accepted";
      case "ADMIN_REJECTED":
        return "Admin Rejected";
      case "INVOICE_SENT":
        return "Invoice Sent";
      case "PAYMENT_RECEIVED":
        return "Payment Received";
      case "IN_TRANSIT":
        return "In Transit";
      case "RE_PROCESSING":
        return "Re-Processing";
      case "IN_PROCESS":
        return "In Process";
      case "SEND_TO_WAREHOUSE":
        return "Send to Warehouse";    
      case "REASSIGN_TO_ADMIN":
        return "Reassign to Admin";
      case "CONFIRMED_FOR_WAREHOUSE":
        return "Confirmed for Warehouse";
      case "IN_STAGE_FOR_WAREHOUSE":
        return "In Stage for Warehouse";
      case "ADMIN_ACCEPTED_FOR_WAREHOUSE":
        return "Admin Accepted for Warehouse";
      case "WAREHOUSE_COMPLETED":
        return "Warehouse Completed";
      case "COMPLETED":
        return "Completed";
      case "DELETED":
        return "Deleted";
      case "CONFIRMED":
        return "Confirmed";
      case "IN_STAGE":
        return "In Stage";
      case "SCHEDULED":
        return "Scheduled";
      case "SOURCE_ACCEPTED":
        return "Source Accepted";
      case "SOURCE_REJECTED":
        return "Source Rejected";
      case "DESTINATION_ACCEPTED":
        return "Destination Accepted";
      case "DESTINATION_REJECTED":
        return "Destination Rejected";
      case "RE_OPEN":
        return "Re-Open";
      default:
        return "Unknown Status";
    }
  });
};

function canAccessRoute(routePath: string): boolean {
  const cookies = new Cookies();
  const userRole = cookies.get("userRole");
  
  if (!userRole) {
    return false;
  }

  const isImpersonating = localStorage.getItem('loginAsUser') === 'yes';
  return RouteAccessControl.isRouteAllowed(routePath, userRole, isImpersonating);
}

function isRouteAllowed(routePath: string, userRole?: string): boolean {
  const cookies = new Cookies();
  const role = userRole || cookies.get("userRole");
  
  if (!role) {
    return false;
  }

  const isImpersonating = localStorage.getItem('loginAsUser') === 'yes';
  return RouteAccessControl.isRouteAllowed(routePath, role, isImpersonating);
}

